import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatDateTR } from "../lib/apiClient";
import { PageHeader, ActionButton } from "../components/Layout";
import { PlusCircle, Search, ArrowRight, ListChecks, X, CheckCircle2, AlertTriangle, FileSpreadsheet, Download, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "../lib/auth";

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadOrders = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders", { params: { include_archived: showArchived } });
      setOrders(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { loadOrders(); /* eslint-disable-next-line */ }, [showArchived]);

  const filtered = orders.filter(o =>
    !q ||
    o.order_number.toLowerCase().includes(q.toLowerCase()) ||
    (o.reference || "").toLowerCase().includes(q.toLowerCase()) ||
    o.customer_name.toLowerCase().includes(q.toLowerCase()) ||
    (o.model || "").toLowerCase().includes(q.toLowerCase())
  );

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(o => o.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const openProductionList = () => {
    if (selected.size === 0) return;
    setPreviewOpen(true);
  };
  const confirmAndNavigate = () => {
    const ids = [...selected].join(",");
    setPreviewOpen(false);
    navigate(`/uretim-listesi?ids=${ids}`);
  };
  const selectedOrders = orders.filter(o => selected.has(o.id));
  const selectedExpectedTotal = selectedOrders.reduce((s, o) => s + (o.total_pieces || 0), 0);
  const selectedPending = selectedOrders.filter(o => !o.barcodes_generated).length;

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/imports/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "marsella_siparis_sablonu.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Şablon indirildi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/imports/orders", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.created_count} sipariş oluşturuldu`);
      await loadOrders();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const canManage = hasRole(user, "production_manager");
  const canDelete = hasRole(user, "admin");

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/orders/${deleteTarget.id}`);
      toast.success(`${deleteTarget.order_number} silindi`);
      setDeleteTarget(null);
      await loadOrders();
    } catch (e) { toast.error(formatApiError(e)); }
    setDeleting(false);
  };

  return (
    <div>
      <PageHeader
        title="Siparişler"
        subtitle="Excel'den toplu yükleyin veya manuel ekleyin."
        actions={
          canManage && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setImportOpen(true)} data-testid="import-excel-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide text-sm">
                <FileSpreadsheet size={18} /> Excel Yükle
              </button>
              <Link to="/siparisler/yeni">
                <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-800 text-slate-900 hover:bg-slate-100 font-bold uppercase tracking-wide text-sm" data-testid="new-order-btn">
                  <PlusCircle size={18} /> Manuel Ekle
                </button>
              </Link>
            </div>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3 max-w-3xl">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sipariş no, referans, müşteri veya model ara..."
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-none focus:border-blue-700 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowArchived(v => !v)}
          data-testid="orders-toggle-archived"
          className={`px-3 py-2.5 border-2 font-bold text-xs uppercase tracking-wide inline-flex items-center gap-1.5 ${
            showArchived
              ? "border-blue-900 bg-blue-900 text-white"
              : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"
          }`}
          title="Tamamı sevk edilmiş siparişler arşivde saklanır"
        >
          <Trash2 size={14}/> {showArchived ? "Arşiv Görünüyor" : "Arşivi Göster"}
        </button>
      </div>

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              {canManage && (
                <th className="w-10 px-3 py-3">
                  <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} data-testid="orders-select-all" className="w-4 h-4" />
                </th>
              )}
              <th className="text-left px-3 py-3 font-bold">Sipariş</th>
              <th className="text-left px-3 py-3 font-bold">Referans</th>
              <th className="text-left px-3 py-3 font-bold">Müşteri</th>
              <th className="text-left px-3 py-3 font-bold">Ölçü</th>
              <th className="text-left px-3 py-3 font-bold">Kumaş</th>
              <th className="text-center px-2 py-3 font-bold" title="Set adedi (Adet)">SET</th>
              <th className="text-left px-2 py-3 font-bold" title="Baza modeli">Baza</th>
              <th className="text-left px-2 py-3 font-bold" title="Başlık modeli">Başlık</th>
              <th className="text-left px-2 py-3 font-bold" title="Yatak modeli">Yatak</th>
              <th className="text-left px-2 py-3 font-bold" title="Komodin modeli">Komodin</th>
              <th className="text-left px-2 py-3 font-bold" title="Bench modeli">Bench</th>
              <th className="text-right px-3 py-3 font-bold">Parça</th>
              <th className="text-left px-3 py-3 font-bold">Sevk</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={14} className="p-8 text-center text-slate-500">Yükleniyor...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={14} className="p-10 text-center text-slate-500">
                <FileSpreadsheet size={40} className="mx-auto mb-2 opacity-40" />
                Henüz sipariş yok. Excel yükleyerek başlayabilirsiniz.
              </td></tr>
            )}
            {filtered.map((o, i) => (
              <tr key={o.id} data-testid={`order-row-${o.order_number}`} onClick={() => navigate(`/siparisler/${o.id}`)} className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50 cursor-pointer`}>
                {canManage && (
                  <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} data-testid={`order-select-${o.order_number}`} className="w-4 h-4" />
                  </td>
                )}
                <td className="px-3 py-3 font-mono font-bold text-blue-700 whitespace-nowrap">
                  {o.order_number}
                  {o.is_archived && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 bg-blue-900 text-white text-[9px] font-bold tracking-widest uppercase align-middle" data-testid={`order-archived-badge-${o.order_number}`}>
                      Arşiv
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-slate-600">{o.reference || "—"}</td>
                <td className="px-3 py-3 font-semibold">{o.customer_name}</td>
                <td className="px-3 py-3 font-mono">{o.size || "—"}</td>
                <td className="px-3 py-3 font-mono text-xs">{o.fabric || "—"}</td>
                <td className="px-2 py-3 text-center font-mono tabular-nums font-bold text-slate-900" data-testid={`order-set-${o.order_number}`}>
                  {o.quantity > 0 ? o.quantity : <span className="text-slate-300">·</span>}
                </td>
                <ModelCell v={o.category_models?.Baza} />
                <ModelCell v={o.category_models?.["Başlık"]} />
                <ModelCell v={o.category_models?.Yatak} />
                <ModelCell v={o.category_models?.Komodin} />
                <ModelCell v={o.category_models?.Bench} />
                <td className="px-3 py-3 text-right font-display font-bold tabular-nums">
                  {o.barcodes_generated ? o.total_pieces : <span className="text-xs text-amber-700 font-semibold">—</span>}
                </td>
                <td className="px-3 py-3 tabular-nums whitespace-nowrap text-xs">{formatDateTR(o.requested_shipment_date)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <Link to={`/siparisler/${o.id}`} className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-bold text-xs mr-2" data-testid={`order-detail-${o.order_number}`}>
                    Detay <ArrowRight size={12} />
                  </Link>
                  {canDelete && (
                    <button
                      onClick={() => setDeleteTarget(o)}
                      data-testid={`order-delete-${o.order_number}`}
                      title="Siparişi Sil"
                      className="inline-flex items-center justify-center w-7 h-7 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                    >
                      <Trash2 size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Selection sticky bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 bg-slate-900 text-white px-6 py-4 border-2 border-slate-700 shadow-2xl flex items-center gap-6" data-testid="bulk-bar">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-70">Seçili Sipariş</div>
            <div className="font-display font-black text-2xl tabular-nums" data-testid="bulk-count">{selected.size}</div>
          </div>
          <button onClick={openProductionList} data-testid="open-production-list-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase tracking-wide text-sm">
            <ListChecks size={18} /> Üretim Listesi Oluştur
          </button>
          <button onClick={clearSelection} data-testid="bulk-clear" className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-sm">
            <X size={16}/> Seçimi Temizle
          </button>
        </div>
      )}

      {/* Excel Import Modal */}
      {importOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => !uploading && setImportOpen(false)} data-testid="import-modal">
          <div className="bg-white border-2 border-slate-900 w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-600 text-white grid place-items-center shrink-0"><FileSpreadsheet size={22}/></div>
              <div className="flex-1">
                <h3 className="font-display font-black text-xl text-slate-900">Excel'den Toplu Sipariş Yükle</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Şablon Excel'i indirin, doldurun ve tekrar yükleyin. Her satır bir sipariş olarak sisteme eklenir.
                </p>
              </div>
              <button onClick={() => !uploading && setImportOpen(false)} className="text-slate-400 hover:text-slate-900"><X size={20}/></button>
            </div>

            <div className="p-4 bg-slate-100 border-2 border-slate-300 mb-4 text-sm">
              <b className="text-slate-900">Beklenen kolonlar:</b>
              <div className="mt-2 font-mono text-xs text-slate-700">
                Müşteri | Referans | Ölçü | Renk | Adet | Baza | Başlık | Yatak | Komodin | Bench
              </div>
              <div className="mt-2 text-xs text-slate-600 space-y-1">
                <div><b>Adet</b> = kaç set/takım sipariş edildiği (örn. 5).</div>
                <div><b>Baza / Başlık / Yatak / Komodin / Bench</b> = o kategori için <b>model adı</b> (örn. <span className="font-mono">Crown</span>). Boş bırakılırsa o kategori sipariş dışıdır.</div>
                <div className="text-slate-500">Örnek: <span className="font-mono">Class wonen | Ahmet | 160x200 | Baby Face v 03 | 5 | Crown | Crown | Crown | — | —</span></div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <button onClick={downloadTemplate} data-testid="download-template-btn" className="inline-flex items-center justify-center gap-2 px-5 py-4 border-2 border-slate-800 hover:bg-slate-900 hover:text-white font-bold uppercase text-sm">
                <Download size={18} /> Şablon İndir
              </button>
              <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-5 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm">
                <Upload size={18} /> {uploading ? "Yükleniyor..." : "Excel Dosyası Seç"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                  disabled={uploading}
                  data-testid="excel-file-input"
                  className="hidden"
                />
              </label>
            </div>

            {importResult && (
              <div className="border-2 border-emerald-500 bg-emerald-50 p-3 text-sm" data-testid="import-result">
                <div className="font-bold text-emerald-900">
                  <CheckCircle2 size={16} className="inline mr-1"/> {importResult.created_count} sipariş başarıyla oluşturuldu.
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2 text-amber-900">
                    <b>Atlanan satırlar:</b>
                    <ul className="list-disc list-inside text-xs mt-1">
                      {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button onClick={() => setImportOpen(false)} disabled={uploading} className="px-5 py-2 border-2 border-slate-300 hover:border-slate-800 font-bold text-sm" data-testid="import-close">
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => !deleting && setDeleteTarget(null)} data-testid="delete-order-modal">
          <div className="bg-white border-2 border-red-700 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-600 text-white grid place-items-center shrink-0"><Trash2 size={22}/></div>
              <div className="flex-1">
                <h3 className="font-display font-black text-xl text-slate-900">Siparişi Sil</h3>
                <p className="text-sm text-slate-600 mt-1">
                  <b className="font-mono">{deleteTarget.order_number}</b> — <b>{deleteTarget.customer_name}</b> siparişi kalıcı olarak silinecek.
                  {deleteTarget.total_pieces > 0 && <> <b className="text-red-700">{deleteTarget.total_pieces}</b> parça ve tüm barkodlar da silinir.</>}
                  <br/>Bu işlem geri alınamaz.
                </p>
              </div>
              <button onClick={() => !deleting && setDeleteTarget(null)} className="text-slate-400 hover:text-slate-900"><X size={20}/></button>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                data-testid="delete-cancel-btn"
                className="px-5 py-2 border-2 border-slate-300 hover:border-slate-800 font-bold text-sm">
                İptal
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                data-testid="delete-confirm-btn"
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase text-sm">
                <Trash2 size={16}/> {deleting ? "Siliniyor..." : "Evet, Sil"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Production list preview modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setPreviewOpen(false)} data-testid="preview-modal">
          <div className="bg-white border-2 border-slate-900 w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-600 text-white grid place-items-center shrink-0"><ListChecks size={22}/></div>
              <div className="flex-1">
                <h3 className="font-display font-black text-xl text-slate-900">Üretim Listesi Önizleme</h3>
                <p className="text-sm text-slate-600 mt-1">Aşağıdaki <b>{selectedOrders.length}</b> sipariş üretim listesine dahil edilecek.</p>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="text-slate-400 hover:text-slate-900" data-testid="preview-close"><X size={20}/></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 border-2 border-slate-800 bg-slate-100">
                <div className="text-[10px] uppercase font-bold text-slate-600">Sipariş</div>
                <div className="font-display font-black text-2xl tabular-nums">{selectedOrders.length}</div>
              </div>
              <div className="p-3 border-2 border-blue-700 bg-blue-50">
                <div className="text-[10px] uppercase font-bold text-blue-700">Toplam Parça</div>
                <div className="font-display font-black text-2xl tabular-nums text-blue-700">{selectedExpectedTotal}</div>
              </div>
              <div className="p-3 border-2 border-amber-500 bg-amber-50">
                <div className="text-[10px] uppercase font-bold text-amber-700">Bekleyen</div>
                <div className="font-display font-black text-2xl tabular-nums text-amber-700">{selectedPending}</div>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto border border-slate-300 mb-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-xs uppercase tracking-widest text-slate-600 border-b border-slate-300">
                    <th className="text-left px-3 py-2">Sipariş No</th>
                    <th className="text-left px-3 py-2">Müşteri</th>
                    <th className="text-left px-3 py-2">Ad / Ölçü</th>
                    <th className="text-left px-3 py-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-200" data-testid={`preview-order-${o.order_number}`}>
                      <td className="px-3 py-2 font-mono font-bold">{o.order_number}</td>
                      <td className="px-3 py-2 font-semibold">{o.customer_name}</td>
                      <td className="px-3 py-2 text-xs">{o.model} · <span className="font-mono">{o.size}</span></td>
                      <td className="px-3 py-2 text-xs">
                        {o.barcodes_generated
                          ? <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><CheckCircle2 size={14}/> Barkod hazır</span>
                          : <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><AlertTriangle size={14}/> Barkod üretilecek</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setPreviewOpen(false)} className="px-4 py-2 border-2 border-slate-300 hover:border-slate-800 font-bold text-sm" data-testid="preview-cancel">İptal</button>
              <button onClick={confirmAndNavigate} data-testid="preview-confirm" className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm">
                <CheckCircle2 size={16}/> Onayla ve Devam Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CatCell({ v }) {
  const n = v || 0;
  return (
    <td className={`px-2 py-3 text-center font-mono tabular-nums font-bold ${n > 0 ? "text-slate-900" : "text-slate-300"}`}>
      {n > 0 ? n : "·"}
    </td>
  );
}

function ModelCell({ v }) {
  const s = (v || "").trim();
  return (
    <td className={`px-2 py-3 text-left text-xs font-semibold ${s ? "text-slate-900" : "text-slate-300"}`} title={s || undefined}>
      {s ? <span className="inline-block max-w-[120px] truncate align-bottom">{s}</span> : "·"}
    </td>
  );
}
